import React from "react";
import { Helmet } from "react-helmet-async";
import { defaultSEO, structuredDataTemplates } from "../../config/seoConfig";

const Seo = ({ 
  title,
  description, 
  keywords,
  image,
  url,
  type = "website",
  structuredData = null,
  noindex = false,
  canonicalUrl = null,
  alternateLanguages = null,
  author = null,
  publishedTime = null,
  modifiedTime = null,
  section = null,
  tags = null,
  twitterCard = "summary_large_image",
  // Legacy support for old 'seo' prop
  seo = null
}) => {
  // Support legacy seo prop structure
  const legacyTitle = seo?.title;
  const legacyDescription = seo?.description;
  const legacyKeywords = seo?.keywords;
  const legacyImage = seo?.image;
  const legacyUrl = seo?.url;

  // Use provided values, fallback to legacy seo prop, then to defaults
  const finalTitle = title || legacyTitle || defaultSEO.title;
  const finalDescription = description || legacyDescription || defaultSEO.description;
  const finalKeywords = keywords || legacyKeywords || defaultSEO.keywords;
  const finalImage = image || legacyImage || `${defaultSEO.siteUrl}${defaultSEO.defaultImage}`;
  const finalUrl = url || legacyUrl || defaultSEO.siteUrl;
  const finalAuthor = author || defaultSEO.author;
  
  // Generate canonical URL if not provided
  const canonicalURL = canonicalUrl || finalUrl;
  
  // Combine structured data
  const combinedStructuredData = {
    ...structuredDataTemplates.organization,
    ...structuredData
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="author" content={finalAuthor} />
      
      {/* Robots and Indexing */}
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />
      <meta name="googlebot" content={noindex ? "noindex, nofollow" : "index, follow"} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalURL} />
      
      {/* Language and Locale */}
      <meta name="language" content={defaultSEO.language} />
      <meta httpEquiv="content-language" content={defaultSEO.language} />
      
      {/* Alternate Languages */}
      {alternateLanguages && alternateLanguages.map((lang, index) => (
        <link key={index} rel="alternate" hrefLang={lang.hreflang} href={lang.href} />
      ))}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:image:secure_url" content={finalImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={finalTitle} />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:url" content={finalUrl} />
      <meta property="og:site_name" content={defaultSEO.siteName} />
      <meta property="og:locale" content={defaultSEO.locale} />
      
      {/* Article specific Open Graph tags */}
      {type === "article" && (
        <>
          {author && <meta property="article:author" content={author} />}
          {publishedTime && <meta property="article:published_time" content={publishedTime} />}
          {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
          {section && <meta property="article:section" content={section} />}
          {tags && tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Twitter Cards */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:site" content={defaultSEO.twitterHandle} />
      <meta name="twitter:creator" content={defaultSEO.twitterHandle} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />
      
      {/* Additional SEO Meta Tags - Note: format-detection, HandheldFriendly, MobileOptimized, apple-mobile-web-app tags are in index.html */}
      <meta name="theme-color" content="#1e40af" />
      
      {/* Structured Data / JSON-LD */}
      {(structuredData || Object.keys(combinedStructuredData).length > 0) && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData || combinedStructuredData)}
        </script>
      )}
      
      {/* Performance and Prefetch Hints */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="dns-prefetch" href="//www.google-analytics.com" />
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      
      {/* Security Headers */}
      <meta httpEquiv="X-Content-Type-Options" content="nosniff" />
      <meta httpEquiv="X-Frame-Options" content="SAMEORIGIN" />
      <meta httpEquiv="X-XSS-Protection" content="1; mode=block" />
      
      {/* Additional Meta Tags for Better SEO */}
      <meta name="rating" content="General" />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="web" />
      <meta name="web_author" content={finalAuthor} />
      <meta name="url" content={finalUrl} />
      <meta name="identifier-URL" content={finalUrl} />
      <meta name="category" content="Travel, Parking, Airport Services" />
      <meta name="coverage" content="Worldwide" />
      <meta name="target" content="all" />
      <meta name="audience" content="all" />
      <meta name="page-type" content="website" />
    </Helmet>
  );
};

export default Seo;
